<template>
  <div
    class="transition-all absolute overflow-x-hidden"
    :class="isOpen &&
      `origin-top-right right-0 mt-1 max-h-60 w-full min-w-44 overflow-y-auto rounded-md
      shadow-lg bg-white dark:bg-navy-800 ring-1 ring-black ring-opacity-5 focus:outline-none z-50
      dark:border-2 dark:border-white/10
      `
    "
    role="menu" ref="menu" aria-orientation="vertical" aria-labelledby="menu-button" tabindex="-1"
    @focus="handleFocus"
  >
    <div
      v-show="isOpen"
      class="relative" role="none"
    >
      <!-- SEARCH INPUT -->
      <div
        v-if="searchable"
        class="w-full flex bg-white dark:bg-navy-800 sticky top-0 left-0 z-50"
      >
        <input
          id="search-input"
          :value="searchVal"
          @input="search"
          @keydown="handleSearchKeyDown"
          :placeholder="searchPlaceholder"
          class="h-10 py-2 px-3 my-0.5 mx-auto rounded border-0 focus:border dark:border-gray-800 focus:outline-none focus:border-primary shadow w-99"
          :class="!isMobile && 'text-sm'"
          ref="searchInput"
        >
      </div>
      <a
        v-for="(option, idx) in options"
        :key="idx" href="#"
        :ref="`option${idx}`"
        class="text-gray-700 dark:text-gray-300 flex items-center px-4 py-3 text-sm truncate w-full group relative"
        :class="optionClass(option, idx)"
        role="menuitem"
        tabindex="-1"
        @click.prevent="select(option)"
        @keydown="handleKeyDown"
      >
        <div
          class="group-hover:opacity-50 group-focus:opacity-50 absolute left-0 h-full w-1 bg-primary"
          :class="(idx === focusedIdx) ? 'opacity-50' : 'opacity-0'"
        />
        <div
          v-if="alignLeft"
          class="flex items-center justify-center shrink-0 w-7 mr-4.5"
        >
          <div
            v-for="(append, appendIdx) in leftAppends(option)"
            :key="`left-${appendIdx}`"
            :class="append.classes"
            class="flex items-center justify-center"
          >
            <font-awesome-icon
              v-if="append.icon"
              :icon="append.icon"
              :size="append.size"
            />
            <component
              v-else-if="append.component"
              :is="append.component"
              v-bind="append.props"
            />
          </div>
        </div>
        <div
          class="truncate min-w-0"
          :class="alignLeft ? 'text-left' : 'text-center'"
        >
          <slot name="option" :option="option">
            <p class="truncate" :class="alignLeft ? 'text-left' : 'text-center'">
              <component
                v-for="part in optionDisplay(option)"
                :is="part.type ? part.type : 'span'"
                :key="part.key"
              >
                {{ part.content }}
              </component>
            </p>
          </slot>
        </div>
        <div
          v-if="rightAppends(option).length"
          class="flex items-center shrink-0"
          :class="alignLeft ? 'pl-2' : 'mx-2'"
        >
          <div
            v-for="(append, appendIdx) in rightAppends(option)"
            :key="`right-${appendIdx}`"
            :class="append.classes"
            class="mx-2"
          >
            <font-awesome-icon
              v-if="append.icon"
              :icon="append.icon"
              :size="append.size"
            />
            <component
              v-else-if="append.component"
              :is="append.component"
              v-bind="append.props"
            />
          </div>
        </div>
      </a>
      <p
        v-if="options.length === 0"
        class="w-full text-sm select-none text-center px-4 py-3 text-gray-700 dark:text-gray-300 cursor-not-allowed tracking-wide font-light"
      >
        No options available
      </p>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex"
import PriorityChip from "@/components/Chips/PriorityChip.vue"
import {
  normalizeDropdownAppends,
  hasLeftDropdownAppendSlot,
} from "./dropdownAppend.js"

export default {
  name: "DropdownMenu",
  components: {
    PriorityChip
  },
  props: {
    options: Array,
    selOption: null,
    isOpen: {
      type: Boolean,
      default: false
    },
    passedKeyDown: String,
    objectIndex: {
      type: Number,
      default: 0
    },
    fields: {
      type: Object,
      default: () => ({ text: "name", value: "id" }),
    },
    searchable: {
      type: Boolean,
      default: true
    },
    searchPlaceholder: {
      type: String,
      default: "Search"
    }
  },
  emits: [
    "search",
    "select",
    "closeDropdown",
    "returnFocus",
  ],
  data() {
    return {
      searchVal: "",
      focusedIdx: 0
    }
  },
  computed: {
    ...mapState(["screen"]),
    isMobile() {
      return /Mobi/i.test(window.navigator.userAgent)
    },
    alignLeft() {
      return this.options.some(option => hasLeftDropdownAppendSlot(option?.dropdownAppend))
    },
  },
  watch: {
    isOpen(val, oldVal) {
      if (val !== oldVal) {
        this.$emit("search", null)
        this.focusedIdx = 0
      }
    }
  },
  mounted() {
    // console.log(this.appendComponent)
  },
  methods: {
    leftAppends(option) {
      return normalizeDropdownAppends(option?.dropdownAppend).left
    },
    rightAppends(option) {
      return normalizeDropdownAppends(option?.dropdownAppend).right
    },
    optionDisplay(option) {
      let str = typeof(option) === "object" ? option[this.fields.text] : option
      if (!str) return ""
      return str.split(/(\^\w+)/g).map((part, index) => {
        if (part.startsWith('^')) {
          return { type: 'sup', content: part.slice(1), key: `sup-${index}` }
        }
        return { type: 'text', content: part, key: `text-${index}` }
      })
    },
    select(option) {
      this.$emit("select", option)
    },
    handleFocus() {
      // do nothing is no key press
      if (this.passedKeyDown === "") return
      // focus on first option if user types arrow down
      else if (this.passedKeyDown === "ArrowDown") {
        if (this.searchable) this.$refs.searchInput.focus()
        else if (this.options[0]) this.focusOnIdx(0)
      }
    },
    handleSearchKeyDown(e) {
      // select "focused" option
      if (e.keyCode === 13 && this.options.length) {
        e.preventDefault()
        this.select(this.options[this.focusedIdx])
      }
      if (e.key === "Tab") {
        this.$emit("closeDropdown")
      }
      // obey keyboard commands once focused on one of the options
      if (e.keyCode === 38) {
        // e.preventDefault()
        if (this.focusedIdx > 0) this.focusOnIdx(this.focusedIdx - 1)
        else this.returnFocusBack()
      }
      if (e.keyCode === 40) {
        // e.preventDefault()
        if (this.options[this.focusedIdx + 1]) this.focusOnIdx(this.focusedIdx + 1)
      }
    },
    handleKeyDown(e) {
      // e.preventDefault()
      if (e.key === "Enter") {
        this.select(this.options[this.focusedIdx])
        this.returnFocusBack()
      }
      if (e.key === "Tab") {
        this.returnFocusBack()
      }
      // obey keyboard commands once focused on one of the options
      if ([38, 40].includes(e.keyCode)) this.moveUpDown(e.keyCode)
    },
    moveUpDown(val) {
      // on arrow up
      if (val === 38) {
        if (this.focusedIdx === 0 && !this.searchable) this.returnFocusBack()
        else if (this.focusedIdx === 1) {
          this.focusedIdx = 0
          this.$refs.searchInput.focus()
        }
        else this.focusOnIdx(this.focusedIdx - 1)
      }
      // else focus on previous or next option
      else if (val === 40 && this.focusedIdx < this.options.length - 1) {
        this.focusOnIdx(this.focusedIdx + 1)
      } 
    },
    focusOnIdx(idx) {
      this.$refs.menu.scrollTop = (idx - 1) * 44
      this.focusedIdx = idx
    },
    returnFocusBack() {
      this.$emit("returnFocus")
      this.focusedIdx = 0
    },
    isSelOption(option) {
      if (typeof(option) === "string") return option === this.selOption
      return option?.[this.fields.value] === this.selOption?.[this.fields.value]
    },
    search(e) {
      this.$emit("search", e.target.value)
      this.searchVal = e.target.value
    },
    optionClass(option, idx) {
      let classes = ""
      classes += this.alignLeft ? "justify-start text-left " : "justify-center text-center "
      classes += (this.focusedIdx === idx)
        ? "bg-gray-100 dark:bg-navy-300 font-semibold focus:outline-none"
        : (this.isSelOption(option)
          ? "font-semibold bg-primary-15 dark:bg-navy-300 hover:bg-gray-100 dark:hover:bg-navy-300 dark:focus:bg-navy-300 focus:bg-gray-100 focus:outline-none"
          : "dark:bg-navy-800 hover:bg-gray-100 dark:hover:bg-navy-650/90 focus:font-semibold focus:bg-gray-100 focus:outline-none"
        )
      return classes
    }
  }
}
</script>

<style scoped>
.w-99 {
  width: 99%;
}
.mr-4\.5 {
  margin-right: 1.125rem; 
}
</style>